import React, { useCallback } from 'react';
import useSWR from 'swr';
import { userLoggedInFetch } from '../../containers/ShopifyContainer';
import { useAppBridge } from '@shopify/app-bridge-react';
type Subscription = {
  id: number
  userId: number,
  planCode: string,
  recurlyId: string | null,
  start: string | null
  end: string | null
  cancelDate: string | null,
  expireDate: string | null,
  trialStartDate: string | null,
  trialEndDate: string | null,
  plan:{
    id:number,
    planCode:string,
    allowedProjects:number
    allowedReports: number // -1 means unlimited
    allowedSubusers: number
    active:boolean,
    price: string // example"11520.99",
    priceUsd: string,
    priceGbp: string,
    priceEur: string
    maxCrawlCredits: number,
    maxKeywords:number,
    billingCycle:'year' | 'month'
  }
}
export type User = {
  userId: number,
  apiKey: string,
  activeShopifySubscription: boolean,
  subscription: null | Subscription
}

interface UserContextValue {
  user?: User | null; // undefined means not loaded yet, null means no user exists yet
  loading: boolean;
  refresh: () => Promise<void>;
}
const UserContext = React.createContext<UserContextValue>({
  user: undefined,
  loading: false,
  refresh: async () => {}
});

type UserProviderProps = React.PropsWithChildren<{ shopifySiteName: string }>;
export function UserProvider ({ children, shopifySiteName }: UserProviderProps) {
  const BASE = window.location.origin;
  const app = useAppBridge();
  const fetch = userLoggedInFetch(app);
  const initUser = useCallback(async () => {
    const apiUrl = new URL(
      '/shopify/init-user',
      BASE
    );
    const userResult = await fetch(apiUrl.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    if (userResult.status !== 200) {
      throw new Error('init-user-failed');
    }
    const result = await userResult.json();
    return result;
  }, [BASE, fetch]);

  const {
    data: userData,
    error,
    mutate: refreshUser
  } = useSWR(shopifySiteName ? `/woorank-api/init-user/${shopifySiteName}` : null, () =>
    initUser()
  );

  const contextValue = {
    user: userData,
    refresh: refreshUser,
    loading: userData === undefined
  };
  return (
    <UserContext.Provider value={contextValue}>
      <>
        {children}
      </>
    </UserContext.Provider>
  );
}

export function useUser (): UserContextValue {
  return React.useContext(UserContext);
}
